import React, {useCallback, useState, useRef} from "react";
import {
    GoogleMap,
    Marker,
    useJsApiLoader,
    Circle
} from "@react-google-maps/api";
import {
    IonContent,
    IonButton,
    IonCheckbox,
    IonLabel,
    IonIcon,
} from "@ionic/react";
import {useHistory} from "react-router";
import {warningOutline, locateOutline} from "ionicons/icons";
import "./ManualLocation.scss";
import {useTranslation} from "react-i18next";
import {useGeolocationPermission} from "../../../hooks/useGeolocationPermission";
import {useStateMachine} from "little-state-machine";
import {MainFooter, MainHeader} from "../../Layout/Layout";
import {updateRdvPoint, updateDetectedPoint} from "../../../state/updateState";
import {Libraries} from "@react-google-maps/api/dist/utils/make-load-script-url";
import {RoutePath} from "../../../model/RoutePath.enum";

const libraries: Libraries = ["places"];

const SWITZERLAND_CENTER = {lat: 46.8182, lng: 8.2275};
const MIN_ZOOM_LEVEL = 15; // Minimum zoom level to allow pin placement
const ACCURACY_THRESHOLD = 20; // meters

const ManualLocation: React.FC = () => {
    const {actions, state} = useStateMachine({updateRdvPoint, updateDetectedPoint});
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [mapClicked, setMapClicked] = useState(false);
    const { t } = useTranslation();
    const [zoomLevel, setZoomLevel] = useState<number>(8);
    const [pinLocation, setPinLocation] = useState<google.maps.LatLngLiteral | null>(null);
    const [circleCenter, setCircleCenter] = useState<google.maps.LatLngLiteral | null>(null);
    const [accuracy, setAccuracy] = useState<number>(0);

    const isZoomedIn = zoomLevel >= MIN_ZOOM_LEVEL;
    const hasPlacedPin = !!pinLocation;
    const canConfirm = isZoomedIn && hasPlacedPin;
    const locationPermission = useGeolocationPermission();
    const rdvPoint = state.rdvPoint;
    const detectedPoint = state.detectedPoint;

    const history = useHistory();

    const {isLoaded} = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY!, // Put your Google Maps API key here
        libraries
    });

    const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
        console.log(locationPermission);
        setMap(mapInstance);
        setZoomLevel(mapInstance.getZoom() || 8);

        if (locationPermission !== "denied" && locationPermission !== "prompt") {
            const locationButton = document.getElementById("retry-gps-icon");
            locationButton &&
            mapInstance &&
            mapInstance.controls[google.maps.ControlPosition.RIGHT_TOP].push(
                locationButton
            );
        }

        mapInstance.addListener("zoom_changed", () => {
            setZoomLevel(mapInstance.getZoom() || 8);
        });

        mapInstance.addListener("click", (e: google.maps.MapMouseEvent) => {
            const currentZoom = mapInstance.getZoom() || 8;
            if (currentZoom >= MIN_ZOOM_LEVEL && e.latLng) {
                const pinPosition = {
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                };
                setPinLocation(pinPosition);
                setMapClicked(true);
                actions.updateRdvPoint(pinPosition);
            }
        });

        if (rdvPoint) {
            mapInstance.setZoom(15);
            setZoomLevel(15);
            setPinLocation(rdvPoint);
            mapInstance.setCenter(rdvPoint);
            setMapClicked(true);
        }

    }, [zoomLevel]);

    const retryLocate = () => {
        if (!navigator.geolocation || !map) {
            console.warn("Geolocation or map not available.");
            return;
        }

        console.log("Requesting geolocation...");

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const {latitude, longitude, accuracy} = position.coords;
                const center = {lat: latitude, lng: longitude};

                console.log("Location success:", position.coords);

                map.panTo(center);
                map.setZoom(17);

                setMapClicked(true);
                setAccuracy(accuracy);
                setCircleCenter(center);

                if (accuracy <= ACCURACY_THRESHOLD) {
                    setPinLocation(center);
                    actions.updateRdvPoint(center);         // Save as RDV point
                    actions.updateDetectedPoint(center);    // Save as detected too
                } else {
                    setPinLocation(null);
                    actions.updateDetectedPoint(center);    // Only as detected point
                }

            },
            (error) => {
                console.error("Geolocation error:", error);
                // TODO: MANAGE ERRRO DISPLAY
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        // User denied permission
                        console.warn("User denied geolocation permission.");
                        // showToast("Permission denied. Please enable location access in your browser settings.");
                        break;

                    case error.POSITION_UNAVAILABLE:
                        console.warn("Location unavailable.");
                        // showToast("Location data unavailable. Please try again later.");
                        break;

                    case error.TIMEOUT:
                        console.warn("Geolocation request timed out.");
                        // showToast("Request timed out. Try again or tap location manually.");
                        break;

                    default:
                        console.warn("Unknown geolocation error.");
                        // showToast("An unexpected error occurred while retrieving your location.");
                        break;
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            }
        );
    };

    return (
        <IonContent className="manualLocationContent">
            <MainHeader>
                <div className="map-instruction-box">
                    <div
                        className={`map-instruction-message ${isZoomedIn ? "ready" : "zoom-in"} animate-pop`}
                    >
                        <IonIcon 
                            src={`${process.env.PUBLIC_URL}/assets/images/information-circle.svg`}
                        />
                        <span>
                            {isZoomedIn
                                ? t("manualLocation_tapOnTheMap")
                                : t("manualLocation_zoomToLocate")}
                        </span>
                    </div>
                </div>

                <div className="map-container">
                    {isLoaded ? (
                        <GoogleMap
                            center={SWITZERLAND_CENTER}
                            zoom={8}
                            mapContainerClassName="map-element"
                            onLoad={onMapLoad}
                            options={{
                                fullscreenControl: false,
                                panControl: false,
                                disableDefaultUI: false,
                                zoomControl: true,
                            }}
                        >
                            {pinLocation && <Marker position={pinLocation}/>}
                            {circleCenter && accuracy > 0 && (
                                <Circle
                                    center={circleCenter}
                                    radius={accuracy}
                                    options={{
                                        fillColor: "rgba(30, 144, 255, 0.2)",
                                        strokeColor: "rgba(30, 144, 255, 0.7)",
                                        strokeWeight: 2,
                                        clickable: false,
                                        draggable: false,
                                        editable: false,
                                    }}
                                />
                            )}
                        </GoogleMap>
                    ) : (
                        <div>Loading Map...</div>
                    )}

                    {locationPermission !== "denied" &&  (
                        <div id="retry-gps-icon" className="retry-gps-icon" onClick={retryLocate}>
                            <IonIcon icon={locateOutline}/>
                        </div>
                    )}
                </div>
            </MainHeader>
            <MainFooter>
                <IonButton className="button-tcs back" expand="block" onClick={() => history.back()}>
                    <IonLabel>{t("Back")}</IonLabel>
                </IonButton>
                <IonButton
                    className="button-tcs cta"
                    color="primary"
                    expand="block"
                    disabled={!hasPlacedPin}
                    onClick={() => {
                        history.push(`${RoutePath.REQUEST}/${RoutePath.CONFIRM_RDV_POINT}`);
                    }}
                >
                    <IonLabel>{hasPlacedPin ? t("manualLocation_confirmLocation") : t("manualLocation_locateFirst")}</IonLabel>
                </IonButton>
            </MainFooter>
        </IonContent>
    );
};

export default ManualLocation;
